import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { ExpenseFormat } from '../interfaces/VehicleBase'
import { transformToExpenseFormat } from '../utils/utils'
import Purchase from '../interfaces/Purchase'
import usePurchaseFetcher from '../hooks/usePurchaseFetcher'
import usePurchaseVoid from '../hooks/usePurchaseVoid'
import { useAuth } from '../hooks/useAuth'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import Client from '../interfaces/Client'
import Vehicle from '../interfaces/Vehicle'
import { AppPath } from '../enums/AppPath'
import { MessageCrea } from '../utils/utils'
import { PurchaseAction } from '../enums/DocActions'
import { UserProfileRole } from '../modules/users/types/UserProfile'

import { Alert, Info } from './MessageInfo'
import PurchasePreview from './PurchasePreview'
import SubMenuPurchase from './SubMenuPurchase'

const PurchaseDetail: React.FC = () => {
  const { fetchPurchase, errorPurchase } = usePurchaseFetcher()
  const { purchaseVoid } = usePurchaseVoid()
  const [purchase, setPurchase] = useState<Purchase>()
  const [localError, setLocalError] = useState<string>()
  const [expenses, setExpenses] = useState<ExpenseFormat[]>([])
  const { userData } = useAuth()
  // Extracting id parameter from the route
  const { id } = useParams<{ id: string }>()
  const [refreshKey, setRefreshKey] = useState(0) // For refreshing data
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const { errorClient, fetchClient } = useClientFetcher()
  const [vehicle, setVehicle] = useState<Vehicle>()
  const [client, setClient] = useState<Client>()
  const navigate = useNavigate()
  const { hasRole } = useAuth()

  useEffect(() => {
    fetchDataAsync()
  }, [id, refreshKey])

  const fetchDataAsync = async () => {
    if (!id) {
      setLocalError('ID is not defined.')

      return // Exit early if `id` is undefined
    }
    const purchase: Purchase | null = await fetchPurchase(id)

    if (!purchase) return
    setPurchase(purchase)

    // Debug logs to investigate the structure
    console.log('Purchase structure:', {
      hasVehicleExpenses: !!purchase.body.vehicle.expenses,
      vehicleExpensesLength: purchase.body.vehicle.expenses?.length || 0,
      hasRootExpenses: 'expenses' in purchase,
      rootExpensesLength: purchase['expenses']?.length || 0,
      total: purchase.total,
      totalInWords: purchase.totalInWords,
    })

    // First check if expenses exist in purchase.body.vehicle
    let expenseArray = purchase.body.vehicle.expenses || []

    // If no expenses found in the standard location, check if they exist directly on the purchase object
    if (expenseArray.length === 0 && purchase.expenses && purchase.expenses.length > 0) {
      console.log('Using expenses from purchase root object:', purchase.expenses.length)
      expenseArray = purchase.expenses
    }

    console.log('Final expenses array length:', expenseArray.length)
    const expenses = transformToExpenseFormat(expenseArray)
    console.log(
      'Transformed expenses:',
      expenses.length,
      expenses.map((e) => ({
        description: e.description,
        cost: e.cost,
        excluded: e.excluded,
      }))
    )
    setExpenses(expenses)

    // Fetch vehicle and client for edit mode
    const vehicleId = purchase.vehicle
    const vehicle = await fetchVehicle(vehicleId)

    if (errorVehicle) {
      setErrorMessage(errorVehicle.message)

      return
    }

    if (!vehicle) {
      setErrorMessage('Error recuperando el vehículo')

      return
    }

    const clientId = purchase.client
    const client = await fetchClient(clientId)

    if (errorClient) {
      setErrorMessage(errorClient.message)

      return
    }

    if (!client) {
      setErrorMessage('Error recuperando el cliente')

      return
    }

    setVehicle(vehicle)
    setClient(client)
  }

  const onAnular = async (motivo: string) => {
    if (!userData?.profile?._id) {
      throw Error('Invalid session')
    }

    const resp = await purchaseVoid(id ?? '', motivo, userData?.profile._id)

    if (resp) {
      window.scrollTo({ top: 0, behavior: 'smooth' })

      const msg = MessageCrea(AppPath.Purchases, PurchaseAction.Anular)
      setInfoMessage(msg)

      // Refresh data
      handleRefresh()
    }
  }

  const handleRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  const infoMessageTimeout = (): void => {
    setInfoMessage('')
  }

  const handleGoBackEdit = () => {
    navigate(`/${AppPath.Purchases}/edit/${purchase?._id}`)
  }

  // Don't render until we have all the required data
  if (!purchase || !vehicle || !client) {
    return (
      <div className="container mx-auto p-4">
        <p>Cargando datos...</p>
        {localError && <p className="text-red-500">{localError}</p>}
        {errorPurchase && <p className="text-red-500">{errorPurchase.message}</p>}
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4">
      {errorMessage !== '' && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      {infoMessage !== '' && <Info message={infoMessage} trigger={Date.now()} onTimeout={infoMessageTimeout} />}

      <div className="flex items-center mb-4">
        <h2 className="text-xl font-semibold">Compra #{purchase.number}</h2>
        {purchase.voidedAt && (
          <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">ANULADA</span>
        )}
      </div>

      {purchase.autoCreated && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-blue-900">Compra Auto-generada</div>
              <div className="text-sm text-blue-700">
                Esta compra fue creada automáticamente por la parte de pago en{' '}
                <a
                  href={`/${AppPath.SaleNotes}/view/${purchase.autoCreated.sourceSaleNoteId}`}
                  className="underline hover:text-blue-900 font-medium"
                >
                  Nota de Venta #{purchase.autoCreated.sourceSaleNoteNumber}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <PurchasePreview
        vehicle={vehicle}
        client={client}
        purchase={purchase}
        costs={expenses}
        consultaSw={true}
        handleAnularClick={hasRole(UserProfileRole.VoidPurchases) ? onAnular : undefined}
      />

      {!purchase.voidedAt && (
        <div className="print:hidden mt-4">
          <SubMenuPurchase
            handleGoBackEdit={handleGoBackEdit}
            handleAnular={onAnular}
            esAnulada={!!purchase.voidedAt}
          />
        </div>
      )}
    </div>
  )
}

export default PurchaseDetail
